import { ChangeDetectionStrategy, Component, OnInit, forwardRef } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { HinDecodeResult, Property, parseHin } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { map, takeUntil } from 'rxjs';
import { createPropertyControl } from '../properties';
import { StaticPropertyFormComponent } from '../static-property-form.component';

type MarineVesselFieldRole = 'hin' | 'manufacturer' | 'serial' | 'buildMonth' | 'modelYear' | 'certYear';

type MarineVesselAssignment = Property.ObjectPropertyAssignment & {
  property: Property.Model<Property.PropertyType>;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-marine-vessel-property-input',
  templateUrl: './marine-vessel-property-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MarineVesselPropertyInputComponent),
    },
    { provide: NG_VALIDATORS, useExisting: MarineVesselPropertyInputComponent, multi: true },
  ],
})
export class MarineVesselPropertyInputComponent extends StaticPropertyFormComponent implements OnInit {
  constructor(private toastSvc: ToastService) {
    super();
  }

  override property!: Property.ObjectModel;

  assignments: MarineVesselAssignment[] = [];
  visibleExpandedAssignments: MarineVesselAssignment[] = [];
  fieldRoles: Partial<Record<MarineVesselFieldRole, MarineVesselAssignment>> = {};

  form = new FormGroup<Record<string, FormControl>>({});
  expanded = false;

  formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

  private lastDecodedHin: string | null = null;

  get hinAssignment(): MarineVesselAssignment | undefined {
    return this.fieldRoles.hin;
  }

  get hinControl(): FormControl | null {
    const assignment = this.hinAssignment;
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey) as FormControl | null;
  }

  getSummaryValue(role: Exclude<MarineVesselFieldRole, 'hin'>): string | number | null {
    const assignment = this.fieldRoles[role];
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey)?.value ?? null;
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);

    if (value && typeof value === 'object') {
      this.form.patchValue(value as Partial<typeof this.form.value>, { emitEvent: false });
      this.syncValidityAfterValueLoad();
    }
  }

  /**
   * Nested required children live on `this.form`. The outer `formControl` only requires the
   * object itself, so incomplete existing vessels would otherwise still look valid.
   */
  override validate(): ValidationErrors | null {
    if (this.form) {
      const formErrors = extractErrors(this.form);
      if (formErrors) {
        this.valid = false;
        return formErrors;
      }
    }
    return super.validate();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.assignments = this.getAssignments();
    this.fieldRoles = this.resolveFieldRoles(this.assignments);
    this.visibleExpandedAssignments = this.assignments.filter(assignment => !this.isHinAssignment(assignment));
    this.form = this.buildForm(this.assignments);
    this.formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

    const existingValue = this.formControl?.value;
    if (existingValue && typeof existingValue === 'object') {
      this.form.patchValue(existingValue as Record<string, unknown>, { emitEvent: false });
    }

    this.syncValidityAfterValueLoad();

    this.hinControl?.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(rawHin => {
      this.tryDecodeHin(String(rawHin ?? ''));
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.formControl.setValue(this.form.value);
    });
  }

  private syncValidityAfterValueLoad() {
    if (!this.form || this.form.valid) return;

    this.expanded = true;
    this.form.markAllAsTouched();
    this.formControl.updateValueAndValidity({ emitEvent: false });
  }

  toggle() {
    this.expanded = !this.expanded;
  }

  private getAssignments(): MarineVesselAssignment[] {
    return (this.property.properties ?? [])
      .filter((assignment: Property.ObjectPropertyAssignment): assignment is MarineVesselAssignment =>
        Boolean(assignment.property)
      )
      .sort((a: MarineVesselAssignment, b: MarineVesselAssignment) => a.order - b.order);
  }

  private buildForm(assignments: MarineVesselAssignment[]): FormGroup<Record<string, FormControl>> {
    const controls = assignments.reduce<Record<string, FormControl>>((current, assignment) => {
      const control = createPropertyControl(assignment.property, assignment.required);

      if (this.fieldRoles.modelYear?.property.valueKey === assignment.property.valueKey) {
        control.addValidators([Validators.min(1000), Validators.max(9999)]);
      }

      if (this.fieldRoles.hin?.property.valueKey === assignment.property.valueKey) {
        control.addValidators(this.hinValidator());
      }

      current[assignment.property.valueKey] = control;
      return current;
    }, {});

    return new FormGroup(controls);
  }

  private resolveFieldRoles(
    assignments: MarineVesselAssignment[]
  ): Partial<Record<MarineVesselFieldRole, MarineVesselAssignment>> {
    return assignments.reduce<Partial<Record<MarineVesselFieldRole, MarineVesselAssignment>>>((roles, assignment) => {
      const role = this.resolveAssignmentRole(assignment);
      if (role && !roles[role]) roles[role] = assignment;

      return roles;
    }, {});
  }

  private resolveAssignmentRole(assignment: MarineVesselAssignment): MarineVesselFieldRole | null {
    const labels = [assignment.displayLabel, assignment.property.name, assignment.property.valueKey]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map(value => value.trim().toLowerCase());

    if (labels.some(value => value === 'hin' || value.endsWith('hin'))) return 'hin';
    if (labels.some(value => value === 'manufacturer' || value.endsWith('manufacturer'))) return 'manufacturer';
    if (labels.some(value => value === 'serial' || value.endsWith('serial') || value === 'serial number')) {
      return 'serial';
    }
    if (
      labels.some(value => value === 'build month' || value.endsWith('buildmonth') || value.endsWith('build_month'))
    ) {
      return 'buildMonth';
    }
    if (labels.some(value => value === 'model year' || value.endsWith('modelyear') || value.endsWith('model_year'))) {
      return 'modelYear';
    }
    if (labels.some(value => value === 'cert year' || value.endsWith('certyear') || value.endsWith('cert_year'))) {
      return 'certYear';
    }

    return null;
  }

  private isHinAssignment(assignment: MarineVesselAssignment): boolean {
    return this.fieldRoles.hin?.property.valueKey === assignment.property.valueKey;
  }

  private hinValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = this.normalizeHin(String(control.value ?? ''));
      if (!value) return null;
      if (value.length !== 12) return { invalidHin: true };

      return parseHin(value) ? null : { invalidHin: true };
    };
  }

  private async tryDecodeHin(rawHin: string) {
    const hin = this.normalizeHin(rawHin);

    if (!hin || hin.length !== 12) {
      this.lastDecodedHin = null;
      return;
    }

    if (this.lastDecodedHin === hin) return;

    const parsed = parseHin(hin);
    if (!parsed) {
      this.lastDecodedHin = null;
      this.toastSvc.show({
        title: 'Invalid HIN',
        text: 'Could not decode this HIN. Verify the value and try again.',
        type: 'warning',
      });
      return;
    }

    this.lastDecodedHin = hin;

    // Loaded lazily: the MIC table is ~500 KB and would otherwise sit in the initial bundle.
    let MIC_LOOKUP: Record<string, string>;
    try {
      ({ MIC_LOOKUP } = await import('@vsolv/packages/properties/domain/mic-lookup'));
    } catch {
      // Network error, or a stale tab requesting a chunk the latest deploy no longer serves.
      if (this.lastDecodedHin !== hin) return;

      this.lastDecodedHin = null;
      this.toastSvc.show({
        title: 'Manufacturer lookup unavailable',
        text: 'Could not load the manufacturer list. Check your connection or reload the page, then re-enter the HIN.',
        type: 'warning',
      });
      return;
    }

    // The HIN changed while the table was loading; that newer value owns the form now.
    if (this.lastDecodedHin !== hin) return;

    const manufacturer = MIC_LOOKUP[parsed.mic];
    if (!manufacturer) {
      this.toastSvc.show({
        title: 'Manufacturer not found',
        text: `Manufacturer code ${parsed.mic} was not recognized.`,
        type: 'warning',
      });
    }

    this.form.patchValue(this.buildHinPatch({ ...parsed, manufacturer: manufacturer ?? parsed.mic }, hin));
  }

  private buildHinPatch(decoded: HinDecodeResult, hin: string): Record<string, string | number | null> {
    const patch: Record<string, string | number | null> = {};

    this.setPatchValue(patch, this.fieldRoles.hin, hin);
    this.setPatchValue(patch, this.fieldRoles.manufacturer, decoded.manufacturer || null);
    this.setPatchValue(patch, this.fieldRoles.serial, decoded.serialNumber || null);
    this.setPatchValue(patch, this.fieldRoles.buildMonth, decoded.buildMonth || null);
    this.setPatchValue(patch, this.fieldRoles.modelYear, decoded.modelYear || null);
    this.setPatchValue(patch, this.fieldRoles.certYear, decoded.certYear || null);

    return patch;
  }

  private setPatchValue(
    patch: Record<string, string | number | null>,
    assignment: MarineVesselAssignment | undefined,
    value: string | number | null
  ) {
    if (!assignment) return;
    patch[assignment.property.valueKey] = value;
  }

  private normalizeHin(hin: string): string {
    return (hin || '').trim().toUpperCase();
  }
}
